import React from 'react';

interface GaugeChartProps {
  value: number;
  max?: number;
  label: string;
  color?: 'blue' | 'green' | 'yellow' | 'red';
  showPercentage?: boolean;
}

const STROKES = { blue: '#0ea5e9', green: '#10b981', yellow: '#f59e0b', red: '#ef4444' };
const TEXTS   = { blue: '#0369a1', green: '#065f46', yellow: '#92400e', red: '#991b1b' };
const BGS     = { blue: '#f0f9ff', green: '#ecfdf5', yellow: '#fffbeb', red: '#fef2f2' };

export default function GaugeChart({
  value,
  max = 100,
  label,
  color = 'blue',
  showPercentage = true,
}: GaugeChartProps) {
  const pct = Math.min(Math.max((value / max) * 100, 0), 100);

  // Geometry
  const cx = 90, cy = 90, r = 64;
  const circ     = 2 * Math.PI * r;   // full circumference ≈ 402.1
  const halfCirc = circ / 2;          // 180° arc length   ≈ 201.1
  const fillLen  = (pct / 100) * halfCirc;

  // Both arcs use the same strokeDasharray trick:
  //   rotate(180) → the default "start at 3 o'clock" shifts to "start at 9 o'clock"
  //   dash  = length we want colored
  //   gap   = rest of circle (large enough to hide remaining stroke)
  const trackDash = `${halfCirc} ${circ}`;
  const fillDash  = `${fillLen}  ${circ}`;

  const transform = `rotate(180, ${cx}, ${cy})`;

  return (
    <div className="flex flex-col items-center select-none">
      {/* SVG: only top half of circle is visible (cy=90 clips bottom) */}
      <div className="relative" style={{ width: 180, height: 108 }}>
        <svg
          width={180}
          height={108}
          viewBox="0 0 180 108"
          overflow="hidden"
        >
          {/* ── track (grey 180° arc) ── */}
          <circle
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={13}
            strokeLinecap="round"
            strokeDasharray={trackDash}
            transform={transform}
          />

          {/* ── fill (colored 0–100% of 180° arc) ── */}
          {pct > 0 && (
            <circle
              cx={cx} cy={cy} r={r}
              fill="none"
              stroke={STROKES[color]}
              strokeWidth={13}
              strokeLinecap="round"
              strokeDasharray={fillDash}
              transform={transform}
              style={{
                filter: `drop-shadow(0 0 5px ${STROKES[color]}66)`,
                transition: 'stroke-dasharray 0.6s ease',
              }}
            />
          )}

          {/* ── centre percentage ── */}
          <text
            x={cx}
            y={82}
            textAnchor="middle"
            dominantBaseline="auto"
            fontSize={28}
            fontWeight={900}
            fill={TEXTS[color]}
            fontFamily="inherit"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {showPercentage ? `${pct.toFixed(0)}%` : value.toLocaleString()}
          </text>
        </svg>
      </div>

      {/* label pill */}
      <div
        className="mt-1 text-xs font-semibold px-3 py-1 rounded-full"
        style={{ background: BGS[color], color: TEXTS[color] }}
      >
        {label}
      </div>
    </div>
  );
}
